import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { FileType, ClipboardCheck, Search, FolderKanban } from "lucide-react";

export default function VerificationReports() {
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch { toast.error("Failed to load projects"); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const filtered = projects.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return p.code?.toLowerCase().includes(s) || p.name?.toLowerCase().includes(s);
  });

  const openPreview = async (project) => {
    setPreviewing(project);
    setPreview(null);
    setBusy(true);
    try {
      const { data } = await api.get(`/reports/verification/${project.id}?format=json`);
      setPreview(data);
    } catch { toast.error("Failed to load preview"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Verification Reports"
        subtitle="Per-project excavation → haul → placement chain-of-custody"
        actions={
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter projects…"
              className="pl-9 pr-3 py-2 border border-gray-300 text-sm w-64"
              data-testid="verification-search"
            />
          </div>
        }
      />

      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Projects list */}
          <div className="md:col-span-1 space-y-3" data-testid="verification-projects-container">
            {filtered.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-12 bg-white border border-gray-200">
                <FolderKanban className="w-8 h-8 text-gray-400 mx-auto mb-3" /> No projects.
              </div>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => openPreview(p)}
                data-testid={`verification-project-${p.code}`}
                className={`w-full text-left bg-white border p-4 hover:border-gray-900 transition-colors ${previewing?.id === p.id ? "border-gray-900" : "border-gray-200"}`}
              >
                <div className="font-mono text-xs text-gray-500">{p.code}</div>
                <div className="font-display font-bold text-gray-900">{p.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Consumed <span className="font-mono text-gray-900">{(p.consumed_tons || 0).toFixed(1)}t</span>
                  {" · "}Excavation Budget <span className="font-mono text-gray-900">{(p.material_budget_tons || 0).toLocaleString()}t</span>
                </div>
              </button>
            ))}
          </div>

          {/* Preview pane */}
          <div className="md:col-span-2">
            {!previewing && (
              <div className="text-center text-sm text-gray-500 py-16 bg-white border border-gray-200">
                <ClipboardCheck className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                Select a project to preview its verification report.
              </div>
            )}
            {previewing && (
              <div className="bg-white border border-gray-200">
                <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
                  <div>
                    <div className="font-mono text-xs text-gray-500">{previewing.code}</div>
                    <div className="font-display font-bold text-xl text-gray-900">{previewing.name}</div>
                    <div className="text-xs text-gray-500">Verification chain-of-custody</div>
                  </div>
                  <a
                    href={`${backend}/api/reports/verification/${previewing.id}?format=pdf`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="download-verification-pdf"
                    className="flex items-center gap-1.5 px-3 py-2 border border-orange-500 text-orange-700 bg-orange-50 text-[11px] uppercase tracking-widest font-semibold hover:bg-orange-500 hover:text-white"
                  >
                    <FileType className="w-3.5 h-3.5" /> Download PDF
                  </a>
                </div>

                <div className="p-5 space-y-6">
                  {busy && <div className="text-center text-sm text-gray-500 py-8">Loading…</div>}
                  {preview && (
                    <>
                      {/* KPIs */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Stat label="Excavated (t)" value={(preview.excavation?.total_tons || 0).toFixed(1)} />
                        <Stat label="Net Placed (t)" value={(preview.excavation?.net_placed_tons || 0).toFixed(1)} />
                        <Stat label="Hauls" value={(preview.hauls || []).length} />
                        <Stat label="Destination bins" value={(preview.bin_totals || []).length} />
                      </div>

                      {/* Excavation source */}
                      <Section title="Excavation Source">
                        {(preview.excavation?.sites || []).length === 0 ? (
                          <Empty text="No delivered loads yet." />
                        ) : (
                          <MiniTable
                            testid="verification-exc-table"
                            columns={["Site", "Address", "Tons", "Yards"]}
                            rows={(preview.excavation?.sites || []).map((s) => [
                              s.name, s.address, `${(s.tons || 0).toFixed(1)}`, `${(s.volume_yards || 0).toFixed(1)}`,
                            ])}
                          />
                        )}
                      </Section>

                      {/* Hauls */}
                      <Section title="Haul Manifest">
                        {(preview.hauls || []).length === 0 ? (
                          <Empty text="No hauls yet." />
                        ) : (
                          <MiniTable
                            testid="verification-hauls-table"
                            columns={["BOL", "Delivered", "Dropoff", "Bin", "Lab", "Tons", "Status"]}
                            rows={(preview.hauls || []).map((h) => [
                              h.bol_number,
                              h.delivered_at ? h.delivered_at.slice(0, 10) : "—",
                              h.dropoff_site_name,
                              h.bin_number || "—",
                              h.lab_result ? `${h.lab_result}${h.lab_date ? " · " + h.lab_date : ""}` : "—",
                              `${(h.weight_tons || 0).toFixed(1)}`,
                              (h.status || "").toUpperCase(),
                            ])}
                          />
                        )}
                      </Section>

                      {/* Bins */}
                      <Section title="Net Tonnage Placed by Bin">
                        {(preview.bin_totals || []).length === 0 ? (
                          <Empty text="No destination bins recorded." />
                        ) : (
                          <MiniTable
                            testid="verification-bins-table"
                            columns={["Bin", "Site", "Classification", "In (t)", "Out (t)", "Current (t)", "Latest Lab"]}
                            rows={(preview.bin_totals || []).map((b) => [
                              b.bin_number, b.site_name, b.classification,
                              `${(b.tons_in || 0).toFixed(1)}`,
                              `${(b.tons_out || 0).toFixed(1)}`,
                              `${(b.current_tons || 0).toFixed(1)}`,
                              b.latest_lab || "—",
                            ])}
                          />
                        )}
                      </Section>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-gray-200 p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className="font-display font-black text-2xl text-gray-900">{value}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div>
      <div className="font-display font-bold text-sm text-gray-700 mb-2 uppercase tracking-widest">{title}</div>
      {children}
    </div>
  );
}
function Empty({ text }) {
  return <div className="text-center text-xs text-gray-500 py-6 border border-dashed border-gray-200">{text}</div>;
}
function MiniTable({ columns, rows, testid }) {
  return (
    <div className="border border-gray-200 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-gray-900 text-white">
          <tr>
            {columns.map((c, i) => (
              <th key={i} className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="odd:bg-gray-50 border-b border-gray-100">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-1.5 font-mono text-xs">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
